import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';
import { ArrowLeft, Camera, Plus, MapPin, Edit, Trash2, Home, Loader2, Phone, CheckCircle, AlertTriangle, Search } from 'lucide-react';
import { resolveEatzyMediaUrl } from '@/lib/utils';
import { AuthenticatedAvatarImage } from '@/components/AuthenticatedAvatarImage';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useUserProfile, type SavedAddress, type SavedPhone } from '@/hooks/useUserProfile';
import { generateDisplayName, getAvatarInitials } from '@shared/utils/displayName';

interface DeliveryAddress {
  id: string;
  label: string;
  street: string;
  aptUnit?: string;
  buildingName?: string;
  doorCode?: string;
  notes?: string;
  isDefault: boolean;
}

interface GooglePlaceResult {
  placeId: string;
  description: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  latitude: number;
  longitude: number;
}

interface AddressFormState {
  label: string;
  street: string;
  aptUnit: string;
  buildingName: string;
  doorCode: string;
  notes: string;
  isDefault: boolean;
  city: string;
  state: string;
  zipCode: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
}

export default function SettingsPersonal() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { profile, addresses: savedAddresses, phones: savedPhones, primaryAddress, isLoading, refreshProfile, refreshAddresses, refreshPhones, cleanupAddresses, deleteAddress, setPrimaryAddress, addAddress, updateAddress
  } = useUserProfile();
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    location: '',
    bio: '',
    avatar: '',
  });

  useEffect(() => {
    if (profile) {
      const defaultDisplayName = generateDisplayName(
        profile.firstName,
        profile.lastName,
        profile.name
      );
      setFormData({
        name: profile.name || '',
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        displayName: profile.displayName || defaultDisplayName,
        email: profile.email || '',
        location: primaryAddress 
          ? `${primaryAddress.city}${primaryAddress.state ? `, ${primaryAddress.state}` : ''}`
          : '',
        bio: profile.bio || '',
        avatar: profile.profilePicture || '',
      });
    }
  }, [profile, primaryAddress]);

  const addresses: DeliveryAddress[] = savedAddresses.map((addr) => ({
    id: String(addr.id),
    label: addr.label || 'Address',
    street: `${addr.streetAddress}, ${addr.city}${addr.state ? `, ${addr.state}` : ''} ${addr.zipCode}`,
    aptUnit: addr.apartment,
    buildingName: addr.businessName,
    doorCode: addr.doorCode,
    notes: addr.deliveryInstructions,
    isDefault: addr.isPrimary,
  }));

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<DeliveryAddress | null>(null);
  const [addressForm, setAddressForm] = useState<AddressFormState>({
    label: '',
    street: '',
    aptUnit: '',
    buildingName: '',
    doorCode: '',
    notes: '',
    isDefault: false,
    city: '',
    state: '',
    zipCode: '',
  });
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [addressSearchResults, setAddressSearchResults] = useState<GooglePlaceResult[]>([]);
  const [isAddressSearching, setIsAddressSearching] = useState(false);
  const [addressPlaceSelected, setAddressPlaceSelected] = useState(false);
  const addressDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Phone modal state
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [phoneForm, setPhoneForm] = useState({
    phoneNumber: '',
    countryCode: '+1',
    isPrimary: false,
  });
  const [isAddingPhone, setIsAddingPhone] = useState(false);
  
  // Avatar upload state
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  
  // Account deletion state
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isCancellingDeletion, setIsCancellingDeletion] = useState(false);
  
  // Fetch deletion status
  const { data: deletionStatus, refetch: refetchDeletionStatus } = useQuery<{
    status: string;
    scheduledAt: string | null;
    requestedAt: string | null;
    gracePeriodDays: number;
  }>({
    queryKey: ['/api/account/deletion-status'],
    enabled: !!profile,
  });
  
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast({
        title: 'Confirmation required',
        description: 'Please type DELETE to confirm account deletion.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsDeletingAccount(true);
    try {
      const response = await apiRequest('POST', '/api/account/delete', { confirmation: 'DELETE' });
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Account deletion scheduled',
          description: data.message,
        });
        setIsDeleteAccountModalOpen(false);
        setDeleteConfirmation('');
        refetchDeletionStatus();
      } else {
        throw new Error(data.error || 'Failed to schedule deletion');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to schedule account deletion',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingAccount(false);
    }
  };
  
  const handleCancelDeletion = async () => {
    setIsCancellingDeletion(true);
    try {
      const response = await apiRequest('POST', '/api/account/delete/cancel', {});
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Deletion cancelled',
          description: 'Your account is now active.',
        });
        refetchDeletionStatus();
      } else {
        throw new Error(data.error || 'Failed to cancel deletion');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel deletion request',
        variant: 'destructive',
      });
    } finally {
      setIsCancellingDeletion(false);
    }
  };
  
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPEG, PNG, GIF, or WebP image.',
        variant: 'destructive',
      });
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const token = localStorage.getItem('auth_token');
      const eatzyToken = localStorage.getItem('eatzyUserToken');
      if (!token || token === 'null' || token === 'undefined') {
        throw new Error('Not authenticated. Please sign in again.');
      }
      const response = await fetch('/api/user/avatar', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(eatzyToken ? { 'x-eatzy-token': eatzyToken } : {}),
        },
        credentials: 'include',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local state immediately for instant feedback
        setFormData(prev => ({ ...prev, avatar: data.avatarUrl }));
        await refreshProfile();
        toast({
          title: 'Avatar updated',
          description: 'Your profile picture has been updated.',
        });
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload avatar');
      }
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload avatar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiRequest('PUT', '/api/user/profile', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: formData.displayName,
        bio: formData.bio,
      });

      if (response.ok) {
        await refreshProfile();
        toast({
          title: 'Profile updated',
          description: 'Your personal information has been saved.',
        });
      } else {
        throw new Error('Failed to save profile');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save your profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleAddPhone = async () => {
    if (!phoneForm.phoneNumber.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a phone number.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsAddingPhone(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token || token === 'null' || token === 'undefined') {
        throw new Error('Not authenticated. Please sign in again.');
      }
      const response = await fetch('/api/user/phones', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          phoneNumber: phoneForm.phoneNumber.trim(),
          countryCode: phoneForm.countryCode,
          isPrimary: phoneForm.isPrimary,
        }),
      });
      
      if (response.ok) {
        await refreshPhones();
        setIsPhoneModalOpen(false);
        setPhoneForm({ phoneNumber: '', countryCode: '+1', isPrimary: false });
        toast({
          title: 'Phone added',
          description: 'Your phone number has been added.',
        });
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add phone number');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add phone number. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsAddingPhone(false);
    }
  };

  const fetchAddressAutocomplete = useCallback(async (query: string) => {
    if (query.trim().length < 3) {
      setAddressSearchResults([]);
      setIsAddressSearching(false);
      return;
    }
    setIsAddressSearching(true);
    try {
      const params = new URLSearchParams({ q: query });
      const response = await fetch(`/api/address/autocomplete?${params}`);
      if (response.ok) {
        const data = await response.json();
        setAddressSearchResults(data.results || []);
      }
    } catch {
      // silently ignore
    } finally {
      setIsAddressSearching(false);
    }
  }, []);

  useEffect(() => {
    if (addressPlaceSelected) return;
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    if (addressSearchQuery.trim().length < 3) {
      setAddressSearchResults([]);
      return;
    }
    addressDebounceRef.current = setTimeout(() => {
      fetchAddressAutocomplete(addressSearchQuery);
    }, 350);
    return () => {
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    };
  }, [addressSearchQuery, addressPlaceSelected, fetchAddressAutocomplete]);

  const handleAddressPlaceSelect = (place: GooglePlaceResult) => {
    setAddressSearchQuery(place.streetAddress);
    setAddressSearchResults([]);
    setAddressPlaceSelected(true);
    setAddressForm(prev => ({
      ...prev,
      street: place.streetAddress,
      city: place.city,
      state: place.state,
      zipCode: place.zipCode,
      latitude: place.latitude,
      longitude: place.longitude,
      placeId: place.placeId,
    }));
  };

  const handleOpenAddressModal = (address?: DeliveryAddress) => {
    if (address) {
      setEditingAddress(address);
      const src = savedAddresses.find(a => String(a.id) === address.id);
      setAddressForm({
        label: address.label || '',
        street: src?.streetAddress || address.street || '',
        aptUnit: address.aptUnit || src?.apartment || '',
        buildingName: address.buildingName || src?.businessName || '',
        doorCode: address.doorCode || src?.doorCode || '',
        notes: address.notes || src?.deliveryInstructions || '',
        isDefault: address.isDefault,
        city: src?.city || '',
        state: src?.state || '',
        zipCode: src?.zipCode || '',
        latitude: src?.latitude,
        longitude: src?.longitude,
        placeId: src?.placeId,
      });
      setAddressSearchQuery(src?.streetAddress || address.street || '');
      setAddressPlaceSelected(true);
    } else {
      setEditingAddress(null);
      setAddressForm({
        label: '',
        street: '',
        aptUnit: '',
        buildingName: '',
        doorCode: '',
        notes: '',
        isDefault: false,
        city: '',
        state: '',
        zipCode: '',
      });
      setAddressSearchQuery('');
      setAddressPlaceSelected(false);
    }
    setAddressSearchResults([]);
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.street) {
      toast({
        title: 'Error',
        description: 'Please search for and select a street address',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      if (editingAddress) {
        const addressId = parseInt(editingAddress.id, 10);
        const success = await updateAddress(addressId, {
          label: addressForm.label || undefined,
          streetAddress: addressForm.street,
          apartment: addressForm.aptUnit || undefined,
          businessName: addressForm.buildingName || undefined,
          doorCode: addressForm.doorCode || undefined,
          deliveryInstructions: addressForm.notes || undefined,
          isPrimary: addressForm.isDefault,
          city: addressForm.city,
          state: addressForm.state,
          zipCode: addressForm.zipCode,
          latitude: addressForm.latitude,
          longitude: addressForm.longitude,
          placeId: addressForm.placeId,
          country: 'US',
        });
        if (success) {
          toast({ title: 'Address updated', description: 'Your delivery address has been updated.' });
          setIsAddressModalOpen(false);
          await refreshAddresses();
        } else {
          toast({ title: 'Failed to update', description: 'Could not update the address. Please try again.', variant: 'destructive' });
        }
      } else {
        const saved = await addAddress({
          label: addressForm.label || undefined,
          streetAddress: addressForm.street,
          apartment: addressForm.aptUnit || undefined,
          businessName: addressForm.buildingName || undefined,
          doorCode: addressForm.doorCode || undefined,
          deliveryInstructions: addressForm.notes || undefined,
          isPrimary: addressForm.isDefault,
          city: addressForm.city,
          state: addressForm.state,
          zipCode: addressForm.zipCode,
          latitude: addressForm.latitude,
          longitude: addressForm.longitude,
          placeId: addressForm.placeId,
          country: 'US',
        });
        if (saved) {
          toast({ title: 'Address added', description: 'New delivery address has been saved.' });
          setIsAddressModalOpen(false);
          await refreshAddresses();
        } else {
          toast({ title: 'Failed to add', description: 'Could not save the address. Please try again.', variant: 'destructive' });
        }
      }
    } catch {
      toast({ title: 'Error', description: 'Something went wrong. Please try again.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAddress = async (id: string) => {
    console.log("[SettingsPersonal] handleDeleteAddress called with id:", id);
    try {
      const addressId = parseInt(id, 10);
      console.log("[SettingsPersonal] Parsed addressId:", addressId);
      const success = await deleteAddress(addressId);
      console.log("[SettingsPersonal] deleteAddress result:", success);
      
      if (success) {
        toast({
          title: 'Address deleted',
          description: 'Delivery address has been removed.',
        });
      } else {
        toast({
          title: 'Failed to delete',
          description: 'Could not remove the address. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[SettingsPersonal] Error deleting address:', err);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const addressId = parseInt(id, 10);
      const success = await setPrimaryAddress(addressId);
      
      if (success) {
        toast({
          title: 'Default address updated',
          description: 'Your default delivery address has been changed.',
        });
      } else {
        toast({
          title: 'Failed to update',
          description: 'Could not set as default. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[SettingsPersonal] Error setting default address:', err);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCleanupAddresses = async () => {
    setIsCleaningUp(true);
    try {
      const result = await cleanupAddresses();
      if (result.deletedCount > 0) {
        toast({
          title: 'Addresses cleaned up',
          description: `Removed ${result.deletedCount} duplicate address${result.deletedCount > 1 ? 'es' : ''}. ${result.keptCount} address${result.keptCount !== 1 ? 'es' : ''} remaining.`,
        });
      } else {
        toast({
          title: 'No duplicates found',
          description: 'All your addresses are already unique.',
        });
      }
    } catch (err) {
      toast({
        title: 'Cleanup failed',
        description: 'Could not clean up addresses. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCleaningUp(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/profile')}
          data-testid="button-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-lg font-semibold">Personal Information</h1>
        <div className="w-10" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-6">
        <div className="p-4 space-y-6">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <Skeleton className="w-24 h-24 rounded-full" />
              </div>
              <Card>
                <CardContent className="p-6 space-y-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i}>
                      <Skeleton className="h-4 w-20 mb-2" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {!isLoading && (
            <>
              {/* Avatar Section */}
              <div className="flex justify-center">
                <div className="relative">
                  <Avatar className="w-24 h-24">
                    <AuthenticatedAvatarImage src={resolveEatzyMediaUrl(formData.avatar)} />
                    <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                      {getAvatarInitials(formData.firstName, formData.lastName, formData.name)}
                    </AvatarFallback>
                  </Avatar>
                  {isUploadingAvatar && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                  <input
                    type="file"
                    ref={avatarInputRef}
                    onChange={handleAvatarUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    data-testid="input-avatar-file"
                  />
                  <Button
                    size="icon"
                    variant="secondary"
                    className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={isUploadingAvatar}
                    data-testid="button-change-avatar"
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Personal Information Form */}
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        data-testid="input-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="displayName">Display Name</Label>
                    <Input
                      id="displayName"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      placeholder="How others see your name (e.g., Sarah C.)"
                      data-testid="input-display-name"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This is how your name appears on reviews and activity
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      disabled
                      className="bg-muted"
                      data-testid="input-email"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Email is managed through your Eatzy account
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Phone Numbers</Label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsPhoneModalOpen(true)}
                        data-testid="button-add-phone"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Phone
                      </Button>
                    </div>
                    {savedPhones.length === 0 ? (
                      <div className="text-sm text-muted-foreground py-4 text-center border rounded-md">
                        <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No phone numbers linked</p>
                        <p className="text-xs mt-1">Add a phone number to enable SMS notifications</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {savedPhones.map((phone) => (
                          <div
                            key={phone.id}
                            className="flex items-center justify-between p-3 border rounded-md bg-muted/30"
                            data-testid={`phone-item-${phone.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <Phone className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{phone.countryCode} {phone.phoneNumber}</span>
                              {phone.isPrimary && (
                                <Badge variant="secondary" className="text-xs">Primary</Badge>
                              )}
                              {phone.isVerified && (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      disabled
                      className="bg-muted"
                      data-testid="input-location"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Based on your primary delivery address
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Tell us about yourself..."
                      className="resize-none min-h-24"
                      data-testid="input-bio"
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Delivery Addresses Section */}
          <Card>
            <CardHeader className="p-4 pb-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-lg flex-1 min-w-0">Delivery Addresses</h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {addresses.length > 1 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCleanupAddresses}
                      disabled={isCleaningUp}
                      data-testid="button-cleanup-addresses"
                    >
                      {isCleaningUp ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Cleaning...
                        </>
                      ) : (
                        'Clean Up Duplicates'
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => handleOpenAddressModal()}
                    data-testid="button-add-address"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Address
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-3 space-y-3">
              {addresses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No delivery addresses saved</p>
                  <p className="text-sm mt-1">Add an address to get started</p>
                </div>
              ) : (
                addresses.map((address) => (
                  <div key={address.id} className="border rounded-md p-4 bg-card" data-testid={`address-card-${address.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Home className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium" data-testid={`text-address-label-${address.id}`}>{address.label}</span>
                          {address.isDefault && (
                            <Badge variant="secondary" className="text-xs" data-testid={`badge-default-${address.id}`}>
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-foreground mb-1" data-testid={`text-address-street-${address.id}`}>{address.street}</p>
                        <div className="text-sm text-muted-foreground space-y-0.5">
                          {address.aptUnit && <p data-testid={`text-address-apt-${address.id}`}>Unit: {address.aptUnit}</p>}
                          {address.buildingName && <p data-testid={`text-address-building-${address.id}`}>Building: {address.buildingName}</p>}
                          {address.doorCode && <p data-testid={`text-address-code-${address.id}`}>Door Code: {address.doorCode}</p>}
                          {address.notes && <p className="italic" data-testid={`text-address-notes-${address.id}`}>Note: {address.notes}</p>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleOpenAddressModal(address)}
                          data-testid={`button-edit-address-${address.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteAddress(address.id)}
                          data-testid={`button-delete-address-${address.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {!address.isDefault && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => handleSetDefault(address.id)}
                        data-testid={`button-set-default-${address.id}`}
                      >
                        Set as Default
                      </Button>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Account Section */}
          <Card className="border-destructive/30">
            <CardHeader className="pb-3">
              <h3 className="font-semibold text-lg text-destructive">Account</h3>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Deleting your account will permanently remove your profile information and forfeit all Kudoz points. Your reviews will be anonymized. This action has a 14-day grace period during which you can cancel.
                </p>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteAccountModalOpen(true)}
                  data-testid="button-delete-account"
                >
                  Delete Account
                </Button>
              </div>
              {deletionStatus?.status === 'scheduled' && deletionStatus.scheduledAt && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-destructive">
                    Account deletion scheduled for {new Date(deletionStatus.scheduledAt).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    You can cancel this request before the scheduled date.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelDeletion}
                    disabled={isCancellingDeletion}
                    data-testid="button-cancel-deletion"
                  >
                    {isCancellingDeletion ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Deletion Request'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          {!isLoading && (
            <Button
              className="w-full"
              onClick={handleSave}
              disabled={isSaving}
              data-testid="button-save"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Add/Edit Address Modal */}
      <Dialog open={isAddressModalOpen} onOpenChange={setIsAddressModalOpen}>
        <DialogContent className="sm:max-w-md mx-4 max-h-[90dvh] flex flex-col" data-testid="modal-address">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {editingAddress ? 'Edit Address' : 'Add Delivery Address'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div>
              <Label htmlFor="address-label">Address Label</Label>
              <Input
                id="address-label"
                placeholder="e.g., Home, Work, Mom's place"
                value={addressForm.label}
                onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                data-testid="input-address-label"
              />
            </div>

            <div className="relative">
              <Label htmlFor="address-street">Street Address *</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                {isAddressSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin pointer-events-none" />
                )}
                <Input
                  id="address-street"
                  placeholder="Search for an address…"
                  value={addressSearchQuery}
                  onChange={(e) => {
                    setAddressSearchQuery(e.target.value);
                    setAddressPlaceSelected(false);
                    if (!e.target.value) {
                      setAddressForm(prev => ({ ...prev, street: '', city: '', state: '', zipCode: '', placeId: undefined, latitude: undefined, longitude: undefined }));
                    }
                  }}
                  className="pl-9 pr-9"
                  autoComplete="off"
                  data-testid="input-address-street"
                />
              </div>
              {addressSearchResults.length > 0 && (
                <div className="mt-1 bg-background border rounded-md shadow-md max-h-52 overflow-y-auto" data-testid="address-autocomplete-results">
                  <p className="px-3 pt-2 pb-1 text-xs text-muted-foreground font-medium">Search results</p>
                  {addressSearchResults.map((place) => (
                    <button
                      key={place.placeId}
                      type="button"
                      className="w-full flex items-start gap-3 px-3 py-2 hover-elevate text-left"
                      onClick={() => handleAddressPlaceSelect(place)}
                      data-testid={`address-result-${place.placeId}`}
                    >
                      <MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{place.streetAddress}</p>
                        <p className="text-xs text-muted-foreground truncate">{place.city}{place.state ? `, ${place.state}` : ''} {place.zipCode}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="grid-address-fields">
              <div>
                <Label htmlFor="address-apt">Apt / Unit</Label>
                <Input
                  id="address-apt"
                  placeholder="Apt 5B"
                  value={addressForm.aptUnit}
                  onChange={(e) => setAddressForm({ ...addressForm, aptUnit: e.target.value })}
                  data-testid="input-address-apt"
                />
              </div>
              <div>
                <Label htmlFor="address-building">Building Name</Label>
                <Input
                  id="address-building"
                  placeholder="The Plaza"
                  value={addressForm.buildingName}
                  onChange={(e) => setAddressForm({ ...addressForm, buildingName: e.target.value })}
                  data-testid="input-address-building"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address-code">Door Code</Label>
              <Input
                id="address-code"
                placeholder="#1234"
                value={addressForm.doorCode}
                onChange={(e) => setAddressForm({ ...addressForm, doorCode: e.target.value })}
                data-testid="input-address-code"
              />
            </div>

            <div>
              <Label htmlFor="address-notes">Delivery Notes</Label>
              <Textarea
                id="address-notes"
                placeholder="Ring twice, leave at door, etc."
                value={addressForm.notes}
                onChange={(e) => setAddressForm({ ...addressForm, notes: e.target.value })}
                className="resize-none min-h-20"
                data-testid="input-address-notes"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="address-default"
                checked={addressForm.isDefault}
                onChange={(e) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                className="w-4 h-4"
                data-testid="checkbox-address-default"
              />
              <Label htmlFor="address-default" className="cursor-pointer">
                Set as default delivery address
              </Label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsAddressModalOpen(false)}
                className="flex-1"
                data-testid="button-cancel-address"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAddress}
                className="flex-1"
                disabled={isSaving}
                data-testid="button-save-address"
              >
                {isSaving ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />{editingAddress ? 'Updating…' : 'Adding…'}</>
                ) : (
                  <>{editingAddress ? 'Update' : 'Add'} Address</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Phone Modal */}
      <Dialog open={isPhoneModalOpen} onOpenChange={setIsPhoneModalOpen}>
        <DialogContent className="sm:max-w-md mx-4" data-testid="modal-phone">
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="phone-number">Phone Number *</Label>
              <div className="flex gap-2">
                <Input
                  id="phone-country"
                  value={phoneForm.countryCode}
                  onChange={(e) => setPhoneForm({ ...phoneForm, countryCode: e.target.value })}
                  className="w-20"
                  placeholder="+1"
                  data-testid="input-phone-country"
                />
                <Input
                  id="phone-number"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phoneForm.phoneNumber}
                  onChange={(e) => setPhoneForm({ ...phoneForm, phoneNumber: e.target.value })}
                  className="flex-1"
                  data-testid="input-phone-number"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="phone-primary"
                checked={phoneForm.isPrimary}
                onChange={(e) => setPhoneForm({ ...phoneForm, isPrimary: e.target.checked })}
                className="w-4 h-4"
                data-testid="checkbox-phone-primary"
              />
              <Label htmlFor="phone-primary" className="cursor-pointer">
                Set as primary phone number
              </Label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsPhoneModalOpen(false);
                  setPhoneForm({ phoneNumber: '', countryCode: '+1', isPrimary: false });
                }}
                className="flex-1"
                data-testid="button-cancel-phone"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddPhone}
                className="flex-1"
                disabled={isAddingPhone}
                data-testid="button-save-phone"
              >
                {isAddingPhone ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Phone'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Modal */}
      <Dialog open={isDeleteAccountModalOpen} onOpenChange={setIsDeleteAccountModalOpen}>
        <DialogContent className="sm:max-w-md mx-4" data-testid="modal-delete-account">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-muted-foreground">
                This action will schedule your account for permanent deletion in 14 days. During this grace period, you can cancel the request by logging in.
              </p>
              <ul className="mt-3 text-sm text-muted-foreground space-y-1">
                <li>• Your profile information will be permanently deleted</li>
                <li>• All Kudoz points will be forfeited</li>
                <li>• Your reviews will be anonymized</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="delete-confirmation">
                Type <span className="font-mono font-bold">DELETE</span> to confirm
              </Label>
              <Input
                id="delete-confirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
                className="mt-2"
                data-testid="input-delete-confirmation"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteAccountModalOpen(false);
                  setDeleteConfirmation('');
                }}
                className="flex-1"
                data-testid="button-cancel-delete"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                className="flex-1"
                disabled={isDeletingAccount || deleteConfirmation !== 'DELETE'}
                data-testid="button-confirm-delete"
              >
                {isDeletingAccount ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Account'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
